import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BankSourceProfileDto,
  UpdateBankSourceProfileRequest,
  UpdateBankSourceProfileResult,
} from '@signalvote/shared';
import { authService } from '@/services/authService';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';
import { apiJson } from './apiFetch';

// ============================================================================
// PPR-017 — Edit bank profile (future imports only)
// ============================================================================

export interface BankProfileEditError extends Error {
  code?: string;
  /** True when the server rejected the edit because the profile is pre-shipped. */
  isPreShipped: boolean;
}

export function useBankSourceProfile(profileId: string | null) {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.finance.bankSourceProfiles.detail(profileId ?? ''),
    queryFn: () =>
      apiJson<BankSourceProfileDto>(`/api/finance/bank-source-profiles/${profileId}`, {
        error: 'Failed to load bank profile',
      }),
    enabled: authService.isAuthenticated() && !!selectedCampaign && !!profileId,
  });
}

export function useUpdateBankSourceProfile() {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation<
    UpdateBankSourceProfileResult,
    BankProfileEditError,
    { profileId: string; request: UpdateBankSourceProfileRequest }
  >({
    mutationFn: ({ profileId, request }) =>
      apiJson<UpdateBankSourceProfileResult>(`/api/finance/bank-source-profiles/${profileId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        json: request,
        error: (body, response) => {
          const err = new Error(body.error ?? 'Failed to update bank profile') as BankProfileEditError;
          err.code = body.code;
          err.isPreShipped = response.status === 403;
          return err;
        },
      }),
    onSuccess: () => {
      // Invalidate the profile detail (the form reads it back) and the
      // import surfaces — future imports use the new version, so any
      // cached preview / mapping data should refresh.
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankSourceProfiles.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.finance.bankIngestion.all });
    },
  });

  return { updateProfile: mutateAsync, isPending };
}
